import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form, Card, Spinner } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { updateAmenity, getAmenity } from "../../store/slices/amenity";
import { useDispatch } from "react-redux";
import { uploadFile } from "../../utils/helpers/fileupload";
import { toast } from "react-toastify";
import { FaImage } from "react-icons/fa";
import { IoMdRemoveCircle } from "react-icons/io";

export default function EditAmenities() {
  const user = JSON.parse(localStorage.getItem("user"));
  const token = user?.data?.accessToken;

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();

  const [name, setName] = useState("");
  const [error, setError] = useState("");

  const [icon, setIcon] = useState(null);
  const [preview, setPreview] = useState("");
  const [errors, setErrors] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchAmenity = async () => {
      try {
        const res = await dispatch(getAmenity({ id })).unwrap();

        const data = res?.amenityData?.[0];

        if (data) {
          setName(data.name || "");
          setPreview(data.iconUrl || "");
        }
      } catch (err) {
        toast.error("Failed to fetch amenity details");
      }
    };

    if (id) fetchAmenity();
  }, [dispatch, id]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const maxSize = 5 * 1024 * 1024;

    if (file.size > maxSize) {
      setErrors((prev) => ({
        ...prev,
        image: "Image size must be less than 5MB",
      }));

      return;
    }

    setIcon(file);
    setPreview(URL.createObjectURL(file));

    setErrors((prev) => {
      const copy = { ...prev };
      delete copy.image;
      return copy;
    });
  };

  const removeImage = () => {
    setIcon(null);
    setPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim()) {
      setError("Please enter category name");
      return;
    }

    if (!icon && !preview) {
      setErrors("Please upload an icon");
      return;
    }

    setError("");
    setErrors("");

    try {
      setLoading(true);

      let fileUrl = "";

      if (icon) {
        fileUrl = await uploadFile(icon, token);
      }

      const payload = {
        id,
        name: name.trim(),
        iconUrl: fileUrl,
      };

      await dispatch(updateAmenity(payload)).unwrap();

      toast.success("Amenity updated successfully");

      navigate("/superadmin/amenity");
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to update amenity",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="breadcrumb-bar mb-2">
        <div className="breadcrumb-left">
          <Link to="/superadmin/home" className="breadcrumb-link active">
            Home
          </Link>
          <span className="breadcrumb-separator">|</span>
          <Link className="breadcrumb-link active" to="/superadmin/amenity">
            Amenities
          </Link>
          <span className="breadcrumb-separator">|</span>
          <span className="breadcrumb-link inactive">Edit Amenity</span>
        </div>
      </div>
      <Container fluid className="p-4">
        <Card style={{ border: "none" }}>
          <Card.Body>
            <Form onSubmit={handleSubmit}>
              <Row className="mb-4 align-items-start">
                <Col md={4} sm={12} className="mb-3">
                  <Form.Group>
                    <Form.Label>Name</Form.Label>
                    <div className="position-relative">
                      <Form.Control
                        type="text"
                        placeholder="Enter Category Name"
                        value={name}
                        onChange={(e) => {
                          setName(e.target.value);
                          setError("");
                        }}
                      />
                    </div>

                    {error && (
                      <div className="text-danger small mt-1">{error}</div>
                    )}
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Image</Form.Label>

                    <div
                      className="custom-file-uploader"
                      onClick={() =>
                        document.getElementById("imageUpload").click()
                      }
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const file = e.dataTransfer.files[0];
                        handleImageChange({ target: { files: [file] } });
                      }}
                    >
                      {!preview ? (
                        <div className="upload-placeholder">
                          <div className="upload-icon-box">
                            <FaImage className="upload-icon-white" />
                          </div>
                          <p className="mb-1">Click or Drag Image Here</p>
                        </div>
                      ) : (
                        <div className="image-preview-wrapper">
                          <img
                            src={
                              preview.startsWith("http")
                                ? preview
                                : `${preview}`
                            }
                            alt="preview"
                            className="preview-img"
                          />
                          <IoMdRemoveCircle
                            size={24}
                            className="remove-icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeImage();
                            }}
                          />
                        </div>
                      )}

                      <Form.Control
                        id="imageUpload"
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        hidden
                      />
                    </div>

                    {errors.image && (
                      <small className="text-danger d-block mt-2">
                        {errors.image}
                      </small>
                    )}
                  </Form.Group>
                </Col>
              </Row>
              <div className="d-flex  mb-5 justify-content-center gap-3 mt-5">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </button>

                <button type="submit" className="submit-btn" disabled={loading}>
                  {loading ? (
                    <>
                      <Spinner
                        animation="border"
                        size="sm"
                        variant="light"
                        className="me-2"
                      />
                      Submitting...
                    </>
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
}
